import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Download, Share2, Square, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL, authHeaders } from './potwApi';

const SIZES = [
  { key: 'square', label: 'Square post', Icon: Square },
  { key: 'story', label: 'Story', Icon: Smartphone },
];

export const CardPreview = ({ eventId, week, version, playerName }) => {
  const [size, setSize] = useState('square');
  const [img, setImg] = useState(null);
  const [loading, setLoading] = useState(true);
  const urlRef = useRef('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API_URL}/api/stats/player-of-week/card.png?${new URLSearchParams({ event_id: eventId, week, size })}`, { headers: authHeaders() })
      .then(r => (r.ok ? r.blob() : Promise.reject()))
      .then(blob => {
        if (!alive) return;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = URL.createObjectURL(blob);
        setImg({ blob, url: urlRef.current });
      })
      .catch(() => alive && toast.error('Could not make the image'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [eventId, week, size, version]);

  useEffect(() => () => urlRef.current && URL.revokeObjectURL(urlRef.current), []);

  const fileName = `player-of-the-week-${week}-${size}.png`;
  const file = img && new File([img.blob], fileName, { type: 'image/png' });
  const canShare = file && navigator.canShare && navigator.canShare({ files: [file] });

  const download = () => {
    const a = document.createElement('a');
    a.href = img.url;
    a.download = fileName;
    a.click();
  };
  const share = () => navigator.share({ files: [file], title: `${playerName} — Player of the Week` }).catch(() => {});

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6" data-testid="potw-preview">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {SIZES.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setSize(key)} data-testid={`potw-size-${key}`}
              className={`px-3 py-1.5 text-xs font-bold rounded flex items-center gap-1.5 transition-colors ${size === key ? 'bg-white shadow text-[#00247D]' : 'text-slate-500 hover:text-slate-700'}`}>
              <Icon className="w-3.5 h-3.5" />{label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {canShare && (
            <Button variant="outline" size="sm" onClick={share} data-testid="potw-share-btn"><Share2 className="w-4 h-4 mr-1.5" />Share</Button>
          )}
          <Button size="sm" onClick={download} disabled={!img || loading} className="bg-[#00247D] hover:bg-[#001A5C] text-white" data-testid="potw-download-btn">
            <Download className="w-4 h-4 mr-1.5" />Download
          </Button>
        </div>
      </div>
      <div className="flex justify-center bg-slate-100 rounded-xl p-4 sm:p-6">
        <div className={`relative w-full ${size === 'square' ? 'max-w-[460px] aspect-square' : 'max-w-[280px] aspect-[9/16]'}`}>
          {img && <img src={img.url} alt={`${playerName} Player of the Week`} className="w-full h-full rounded-lg shadow-xl object-contain" data-testid="potw-card-image" />}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-lg" data-testid="potw-card-loading">
              <div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-3 text-center">{size === 'square' ? '1080 × 1080 for Instagram & Facebook posts' : '1080 × 1920 for Instagram & Facebook stories'}</p>
    </div>
  );
};
